import React, { useMemo, useState } from "react";
import coin2k from "../assets/2K.png";
import coin5k from "../assets/5K.png";
import coin10k from "../assets/10K.png";
import coin20k from "../assets/20K.png";
import coin50k from "../assets/50K.png";
import UsersIcon from "../assets/usersIcon.svg";
import QuestionIcon from "../assets/questionIcon.svg";
import HistoryIcon from "../assets/historyIcon.svg";
import SoundUpIcon from "../assets/soundUp.svg";
import SoundMuteIcon from "../assets/soundMute.svg";
import CoinBtn from "./CoinButton";
import DiamondIcon from "../assets/diamond.png";
import audioPath from "../assets/audio/coinsSound.mp3";
import Frame from "./Frame";
import { ToastConent } from "./ToastConent";
import BetHistoryModel from "./BetHistoryModel";

const COIN_IMAGES = [coin2k, coin5k, coin10k, coin20k, coin50k];

const BET_ERROR_MESSAGES = {
  betting_closed: "Betting is closed for this round",
  insufficient_diamonds: "You don't have enough diamonds, please recharge first!",
  no_coin_selected: "Select a coin first",
  too_many_spots: "You've backed the maximum number of squares",
  frame_max_exceeded: "Bet limit reached for this square",
  amount_below_min: "That stake is below the minimum",
  feature_disabled: "Roulette Casino is currently unavailable",
};

/**
 * Deterministic 0..1 PRNG so scattered pot coins keep their positions across
 * re-renders of the same round instead of jumping on every state change.
 */
function seededUnit(seed) {
  let h = 2166136261;
  const str = String(seed);
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
}

/**
 * Visual only: break a square's pot into coin sprites so the table looks like
 * the original. The source scattered one sprite per individual bet, which
 * required broadcasting every player's stake; the server now publishes only
 * per-frame totals, so we depict the same pot with a capped number of sprites.
 */
function potCoins(roundId, frameId, total, denominations, seedTag) {
  if (!total || !denominations.length) return [];
  const coins = [];
  let remaining = total;
  const sorted = [...denominations].sort((a, b) => b - a);
  for (const denom of sorted) {
    while (remaining >= denom && coins.length < 8) {
      coins.push(denom);
      remaining -= denom;
    }
    if (coins.length >= 8) break;
  }
  return coins.map((coin, i) => ({
    coin,
    left: 5 + seededUnit(`${roundId}:${frameId}:${seedTag}:${i}:l`) * 55,
    top: seededUnit(`${roundId}:${frameId}:${seedTag}:${i}:t`) * 45,
  }));
}

export default function TableContent(props) {
  const {
    status,
    round,
    frames,
    quickBets,
    balance,
    betsByFrame,
    myStakes,
    remainingMs,
    numberHistory,
    lastResult,
    muteSound,
    handleMuteSound,
    handleOpenModel,
    onBet,
  } = props;

  const [selectedCoin, setSelectedCoin] = useState(null);
  const [betHistoryOpen, setBetHistoryOpen] = useState(false);

  const bettingOpen = status === "betting" && remainingMs > 0;
  const roundId = round ? round._id : "idle";
  const winningFrames = status === "betting" ? [] : ((round && round.winningFrameIds) || []);

  const effectiveCoin = selectedCoin != null
    ? selectedCoin
    : (quickBets.length ? quickBets[0] : null);

  const playCoinSound = async () => {
    if (muteSound) return;
    try {
      const audio = new Audio(audioPath);
      audio.volume = 1;
      await audio.play();
    } catch (error) {
      // Autoplay can be blocked until the user interacts; not fatal.
    }
  };

  const handleFrameClick = async (frameId) => {
    if (!bettingOpen) {
      ToastConent(BET_ERROR_MESSAGES.betting_closed);
      return;
    }
    playCoinSound();
    const error = await onBet(frameId, effectiveCoin);
    if (error) ToastConent(BET_ERROR_MESSAGES[error] || "Bet failed, please try again");
  };

  const totalBetCoin = useMemo(
    () => Object.values(betsByFrame || {}).reduce((sum, v) => sum + Number(v || 0), 0),
    [betsByFrame]
  );
  const totalMineBetCoin = useMemo(
    () => Object.values(myStakes || {}).reduce((sum, v) => sum + Number(v || 0), 0),
    [myStakes]
  );

  const latestNumber = (numberHistory && numberHistory[0]) || null;
  const resultNo = lastResult || latestNumber || { no: "0", color: "green", oddAndEven: "odd" };

  let timerText;
  if (status === "betting") timerText = `${Math.max(0, Math.ceil(remainingMs / 1000))}s`;
  else if (status === "spinning") timerText = "Spinning";
  else timerText = "Next round";

  return (
    <>
      <div className="table-content">
        <div className="table-data">
          <div className="table-center-data">
            <div className="table-daaaa">
              <div className="table-userButton">
                <div className="row w-100">
                  <div className="col-6">
                    <div className="show-button-top">
                      <div className="helpIcon-button">
                        <button
                          onClick={(event) => {
                            handleOpenModel("rule");
                            event.stopPropagation();
                          }}
                        >
                          <QuestionIcon />
                        </button>
                        <div className="sound-icon-mo">
                          <button onClick={() => handleMuteSound()}>
                            {muteSound ? <SoundMuteIcon /> : <SoundUpIcon />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="user-button">
                      <button>
                        <UsersIcon />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row table-head">
                <div className="col-4 col-md-3 d-flex justify-content-start">
                  <div
                    className="table-oldBet"
                    onClick={(event) => {
                      setBetHistoryOpen((v) => !v);
                      event.stopPropagation();
                    }}
                  >
                    <h6>New &#62;</h6>
                    <div className="old-bet-showNumber">
                      <div
                        className="old-bet-boxNumber"
                        style={{ backgroundColor: `${resultNo.color}` }}
                      >
                        <span>{resultNo.no}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-4 col-md-6 d-flex align-items-center justify-content-center">
                  <div className="table-headText">
                    <h6>
                      Total Bet: <span>{totalBetCoin.toLocaleString()}</span>
                    </h6>
                    <h6>
                      My Bet: <span>{totalMineBetCoin.toLocaleString()}</span>
                    </h6>
                  </div>
                </div>
                <div className="col-4 col-md-3">
                  <div className="table-timeWatch">
                    <div className="time-watch">
                      <span>{timerText}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="table-body">
                <div className="table-box">
                  {frames.map((frame) => {
                    const potTotal = Number(betsByFrame[String(frame.id)] || 0);
                    const mineTotal = Number(myStakes[frame.id] || 0);
                    return (
                      <Frame
                        key={frame.id}
                        multiCoin={String(frame.multiplier)}
                        betDetails={frame.label}
                        coins={potTotal}
                        mine={mineTotal}
                        arrCoins={potCoins(roundId, frame.id, potTotal - mineTotal, quickBets, "pot")}
                        mineCoin={potCoins(roundId, frame.id, mineTotal, quickBets, "mine")}
                        gameCoin={quickBets}
                        framNo={frame.id}
                        winningFrames={winningFrames}
                        onClick={() => handleFrameClick(frame.id)}
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="table-footer">
              <div className="dimond-coin">
                <img src={DiamondIcon} alt="" />
                <span>{balance ? balance.toLocaleString() : "0"}</span>
              </div>
              <div className="coin-button-box">
                {quickBets.map((coin, index) => (
                  <CoinBtn
                    key={coin}
                    src={COIN_IMAGES[index % COIN_IMAGES.length]}
                    number={coin}
                    funcClick={() => setSelectedCoin(coin)}
                    selectedClass={effectiveCoin === coin ? "selectedCoin" : ""}
                    disabledClass={bettingOpen ? "" : "coinDisabled"}
                  />
                ))}
              </div>
              <div className="history-icon">
                <button
                  onClick={(event) => {
                    handleOpenModel("history");
                    event.stopPropagation();
                  }}
                >
                  <HistoryIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BetHistoryModel
        open={betHistoryOpen}
        setOpen={setBetHistoryOpen}
        historyData={numberHistory}
      />
    </>
  );
}
